import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "../components/StaticMotion";
import { HoverCard } from "../components/HoverMotion";
import { Button } from "@vionixasia/shared-components";
import {
  FaUserPlus,
  FaBoxOpen,
  FaHourglassEnd,
  FaAward,
  FaCamera,
  FaMapMarkedAlt,
  FaPaintBrush,
  FaUsers,
} from "react-icons/fa";
import { getServiceBySlug } from "../data/services";
import { LtHero } from "../livetrack/LtHero";
import { LtAbout } from "../livetrack/LtAbout";
import { LtContact } from "../livetrack/LtContact";

const ICON_MAP = {
  registration: <FaUserPlus className="w-5 h-5" />,
  racepack: <FaBoxOpen className="w-5 h-5" />,
  timing: <FaHourglassEnd className="w-5 h-5" />,
  results: <FaAward className="w-5 h-5" />,
  gallery: <FaCamera className="w-5 h-5" />,
  tracking: <FaMapMarkedAlt className="w-5 h-5" />,
  concept: <FaPaintBrush className="w-5 h-5" />,
  fulfilment: <FaUsers className="w-5 h-5" />,
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export const ServicePage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  // Live Tracking: tampilkan konten identik dengan subdomain tracking (livetrack),
  // bukan template service generik.
  if (service.liveContent) {
    return (
      <div className="min-h-screen bg-white">
        <LtHero />
        <LtAbout />
        <LtContact />
      </div>
    );
  }

  const icon = ICON_MAP[service.icon];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="w-full py-20 px-4 bg-slate-900">
        <div className="w-full max-w-6xl mx-auto">
          <div className="mb-8">
            <Link to="/">
              <Button color="slate" variant="ghost" className="text-slate-400 border-slate-700">
                ← Back to Home
              </Button>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-10 h-10 bg-white/10 border border-white/20 rounded-icon flex items-center justify-center text-white mb-6">
              {icon}
            </div>

            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
              Our Services
            </p>

            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 max-w-3xl leading-tight">
              {service.title}
            </h1>

            <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="w-full py-16 px-4 bg-white">
        <div className="w-full max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-2">What's Included</h2>
            <p className="text-slate-600 mb-10 max-w-2xl">
              Every capability built into this service — ready to deploy at your event.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.09 } },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={cardVariants}
              >
                <HoverCard
                  className={`p-4 h-full flex flex-col gap-2 rounded-card border border-slate-200 ${
                    index === 1 ? "bg-slate-900" : "bg-white"
                  }`}
                >
                  <h3
                    className={`text-sm font-bold ${
                      index === 1 ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      index === 1 ? "text-slate-300" : "text-slate-600"
                    }`}
                  >
                    {feature.description}
                  </p>
                </HoverCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* See It Live — embed the actual live platform in a macOS browser frame */}
      {service.liveUrl && (
        <section className="w-full py-16 lg:py-24 px-4 bg-white border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight mb-3">
                {service.liveTitle || "See It Live"}
              </h2>
              {service.liveSubtitle && (
                <p className="text-gray-600 max-w-3xl mx-auto">{service.liveSubtitle}</p>
              )}
            </div>

            <div className="rounded-card border border-slate-200 overflow-hidden bg-white shadow-lg">
              {/* Browser-style top bar (macOS) */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                  <span className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-2 px-3 py-1 bg-white rounded-md border border-slate-200 text-xs text-slate-500 truncate">
                  {service.liveUrl.replace(/^https?:\/\//, "")}
                </div>
                <a
                  href={service.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 whitespace-nowrap"
                >
                  Open ↗
                </a>
              </div>
              <iframe
                src={service.liveUrl}
                title={service.title}
                loading="lazy"
                className="w-full h-[480px] md:h-[620px] bg-white block"
              />
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="w-full py-16 px-4 bg-slate-50 border-t border-slate-200">
        <div className="w-full max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">{service.cta}</h2>
            <p className="text-slate-600 text-sm">
              Contact our team and we'll walk you through setup, deployment, and pricing.
            </p>
          </div>
          <Link to="/" state={{ scrollTo: "contact" }} className="flex-shrink-0">
            <Button color="slate" variant="primary">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
